import clsx from 'clsx';
import React from 'react';
import { match } from 'ts-pattern';

import type { TextSizeType } from '../utils';
import { TextInput } from '../Input/TextInput';
import { sizeToTextClassName } from '../utils';

export const kBoxWidth =
  'w-f15 min-w-f15 largeLaptop:w-f19 largeLaptop:min-w-f19';
export const kHalfBoxWidth =
  'w-f7 min-w-f7 largeLaptop:w-f10 largeLaptop:min-w-f10';

type TextCellProps = {
  text: string;
  secondary?: boolean;
  textSize: TextSizeType;
  align: 'left' | 'right' | 'center';
  border: 'left' | 'right' | 'none';
  onChange?: (text: string) => void;
  fullWidth?: boolean;
  useMonoText?: boolean;
  'data-testid'?: string;
};

export const TextCell = ({
  text,
  secondary,
  textSize,
  align,
  border,
  onChange,
  fullWidth = false,
  useMonoText = false,
  'data-testid': testId,
}: TextCellProps) => (
  <div
    className={clsx([
      'text-md flex flex-col h-full p-f1 box-border',
      fullWidth ? 'w-full min-w-f19' : kBoxWidth,
      border === 'left' && 'border-main border-l-[1px]',
      border === 'right' && 'border-main border-r-[1px]',
      border === 'none' && 'border-none',
      align === 'left' && 'justify-start',
      align === 'center' && 'justify-center',
      align === 'right' && 'justify-start',
      secondary ? 'text-main-500' : 'text-main',
      useMonoText ? 'font-FragmentMono' : 'font-sans',
    ])}
  >
    {match(onChange)
      .when(
        (fn): fn is (text: string) => void => fn !== undefined,
        (fn) => (
          <TextInput
            value={text}
            size={textSize}
            padding="p-0"
            width="w-full"
            height="h-fit"
            align={align}
            placeholder="Enter Invoice ID"
            onChange={(e) => fn(e.target.value)}
            data-testid={testId}
          />
        )
      )
      .otherwise(() => (
        <span
          className={clsx(
            sizeToTextClassName[textSize],
            align === 'left' && 'text-left',
            align === 'center' && 'text-center',
            align === 'right' && 'text-right'
          )}
          data-testid={testId}
        >
          {text}
        </span>
      ))}
  </div>
);

type LayoutCellProps = {
  children: React.ReactNode;
  border: 'left' | 'right' | 'none';
  align?: 'top' | 'center' | 'bottom';
  width?: 'half' | 'standard' | 'full';
  removePadding?: boolean;
};

export const LayoutCell = ({
  children,
  border,
  align = 'center',
  width = 'standard',
  removePadding = false,
}: LayoutCellProps) => (
  <div
    className={clsx([
      'flex flex-col h-full items-start overflow-y-scroll scrollbar-hide',
      !removePadding && 'p-f1',
      width === 'standard' && kBoxWidth,
      width === 'half' && kHalfBoxWidth,
      width === 'full' && 'w-full',
      border === 'left' && 'border-main border-l-[1px]',
      border === 'right' && 'border-main border-r-[1px]',
      border === 'none' && 'border-none',
      align === 'top' && 'justify-start',
      align === 'center' && 'justify-center',
      align === 'bottom' && 'justify-end',
    ])}
  >
    {children}
  </div>
);

type LayoutRowProps = {
  isFirst?: boolean;
  height: 'small' | 'medium' | 'large' | 'xl' | 'fit';
  justify?: 'between' | 'start' | 'end';
  hide?: boolean;
  children: React.ReactNode;
};

export const LayoutRow = ({
  isFirst = false,
  height,
  justify = 'between',
  hide = false,
  children,
}: LayoutRowProps) => (
  <>
    {!hide && (
      <div
        className={clsx([
          'flex flex-row w-full items-start',
          'border-[1px] border-main border-t-0 transition-opacity duration-300',
          isFirst && 'border-t-[1px]',
          justify === 'between' && 'justify-between',
          justify === 'start' && 'justify-start',
          justify === 'end' && 'justify-end',
          height === 'small' && 'h-f4',
          height === 'medium' && 'h-f8',
          height === 'large' && 'h-f12',
          height === 'xl' && 'h-f17',
          height === 'fit' && 'h-fit',
        ])}
      >
        {children}
      </div>
    )}
  </>
);
